import { useEffect, useRef } from "react";

function oncePerSession(key, fn) {
  try {
    if (sessionStorage.getItem(key)) return;
    fn();
    sessionStorage.setItem(key, "1");
  } catch (_) {
    fn();
  }
}

function getOrCreateVisitorId() {
  try {
    let visitorId = localStorage.getItem("visitor_id");
    if (!visitorId) {
      visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("visitor_id", visitorId);
      localStorage.setItem("first_visit", new Date().toISOString());
    }
    return visitorId;
  } catch (_) {
    return `session_${Date.now()}`;
  }
}

function getVisitCount() {
  try {
    const count = parseInt(localStorage.getItem("visit_count") || "0", 10);
    localStorage.setItem("visit_count", String(count + 1));
    return count + 1;
  } catch (_) {
    return 1;
  }
}

function getNetworkInfo() {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      return {
        effectiveType: conn.effectiveType || "unknown",
        downlink: conn.downlink || null,
        rtt: conn.rtt || null,
        saveData: conn.saveData || false,
      };
    }
  } catch (_) {}
  return null;
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let deviceBrand = "Unknown";
  let osVersion = "Unknown";
  let browserVersion = "Unknown";

  // Device brand detection
  if (/iPhone/.test(ua)) deviceBrand = "Apple iPhone";
  else if (/iPad/.test(ua)) deviceBrand = "Apple iPad";
  else if (/Macintosh/.test(ua)) deviceBrand = "Apple Mac";
  else if (/Samsung/.test(ua)) deviceBrand = "Samsung";
  else if (/Huawei/.test(ua)) deviceBrand = "Huawei";
  else if (/Xiaomi/.test(ua)) deviceBrand = "Xiaomi";
  else if (/OnePlus/.test(ua)) deviceBrand = "OnePlus";
  else if (/Pixel/.test(ua)) deviceBrand = "Google Pixel";
  else if (/Windows/.test(ua)) deviceBrand = "Windows PC";
  else if (/Linux/.test(ua)) deviceBrand = "Linux PC";

  // OS version detection
  const osMatch = ua.match(/(?:Windows NT|Mac OS X|Android|iOS) ([\d._]+)/);
  if (osMatch) osVersion = osMatch[1].replace(/_/g, ".");

  // Browser version detection
  const browserMatch = ua.match(/(?:Chrome|Firefox|Safari|Edge)\/([\d.]+)/);
  if (browserMatch) browserVersion = browserMatch[1];

  return { deviceBrand, osVersion, browserVersion };
}

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (_) {
    return "Unknown";
  }
}

export default function VisitNotifier() {
  const sessionStartRef = useRef(Date.now());
  const pagesVisitedRef = useRef([window.location.pathname]);
  const clickCountRef = useRef(0);
  const maxScrollRef = useRef(0);

  useEffect(() => {
    // Track scroll depth
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScrollRef.current) {
        maxScrollRef.current = Math.min(scrollPercent, 100);
      }
    };

    // Track clicks
    const handleClick = (e) => {
      clickCountRef.current++;
      const target = e.target;
      const elementInfo = {
        tag: target.tagName,
        id: target.id || null,
        className: target.className || null,
        text: target.innerText?.substring(0, 50) || null,
      };
      
      // Send click event to API
      fetch("/api/track-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "click",
          element: elementInfo,
          path: window.location.pathname,
        }),
        keepalive: true,
      }).catch(() => {});
    };

    // Track page changes (for SPAs)
    const trackPageChange = () => {
      const currentPath = window.location.pathname;
      if (!pagesVisitedRef.current.includes(currentPath)) {
        pagesVisitedRef.current.push(currentPath);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("click", handleClick, { passive: true });
    window.addEventListener("popstate", trackPageChange);

    // Send initial visit notification
    oncePerSession("notified-visit", () => {
      const visitorId = getOrCreateVisitorId();
      const visitCount = getVisitCount();
      const firstVisit = localStorage.getItem("first_visit");
      const isReturning = visitCount > 1;

      const path = window.location.pathname + window.location.search + window.location.hash;
      const referrer = document.referrer || "";
      const userAgent = navigator.userAgent;
      const language = navigator.language || navigator.languages?.[0] || "";
      const screenStr = `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`;
      
      const networkInfo = getNetworkInfo();
      const { deviceBrand, osVersion, browserVersion } = getDeviceInfo();
      const timezone = detectTimezone();

      const payload = {
        path,
        referrer,
        userAgent,
        language,
        screen: screenStr,
        visitorId,
        visitCount,
        isReturning,
        firstVisit,
        timezone,
        networkInfo,
        deviceBrand,
        osVersion,
        browserVersion,
      };

      console.log("[VisitNotifier] Sending notification:", payload);

      fetch("/api/notify-visit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
        .then((res) => res.json())
        .then((data) => console.log("[VisitNotifier] Success:", data))
        .catch((err) => console.error("[VisitNotifier] Error:", err));
    });

    // Send session end data when user leaves
    const sendSessionEnd = () => {
      const sessionDuration = Math.round((Date.now() - sessionStartRef.current) / 1000);
      const payload = {
        type: "session_end",
        sessionDuration,
        pagesVisited: pagesVisitedRef.current,
        clickCount: clickCountRef.current,
        maxScroll: maxScrollRef.current,
        path: window.location.pathname,
      };

      // Use sendBeacon for reliability on page unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          "/api/track-event",
          JSON.stringify(payload)
        );
      } else {
        fetch("/api/track-event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener("beforeunload", sendSessionEnd);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        sendSessionEnd();
      }
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleClick);
      window.removeEventListener("popstate", trackPageChange);
      window.removeEventListener("beforeunload", sendSessionEnd);
    };
  }, []);

  return null;
}
